import { useState } from 'react'
import { ImageDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { FileUploader } from '@/components/tools/file-uploader'
import { FileInfo } from '@/components/tools/file-info'
import { ProgressBar } from '@/components/tools/progress-bar'
import { ErrorMessage } from '@/components/tools/error-message'
import { useToolStore } from '@/stores/tool-store'
import { compressImage, outputFileName, type ImageOutputFormat } from './lib'
import { ImageResultCard } from './image-result-card'
import { useI18n } from '@/lib/i18n'

const MAX_IMAGE_MB = 50

const FORMAT_OPTIONS = [
  { value: 'webp', labelKey: 'imgFormatWebp' as const },
  { value: 'jpeg', label: 'JPEG' },
  { value: 'png', label: 'PNG' },
] as const satisfies ReadonlyArray<{ value: ImageOutputFormat; label?: string; labelKey?: 'imgFormatWebp' }>

/** Image compression panel: pick → configure → compress → compare. */
export function ImagePanel() {
  const [file, setFile] = useState<File | null>(null)
  const [format, setFormat] = useState<ImageOutputFormat>('webp')
  const [quality, setQuality] = useState(75)
  const { status, progress, error, resultBlob, selectFile, start, setProgress, finish, fail, reset } =
    useToolStore()
  const { t } = useI18n()

  const formatLabel = (value: ImageOutputFormat): string => {
    const option = FORMAT_OPTIONS.find((item) => item.value === value)
    if (!option) return value
    if ('label' in option && option.label) return option.label
    if ('labelKey' in option && option.labelKey) return t(option.labelKey)
    return value
  }

  const handleFile = (selected: File) => {
    setFile(selected)
    selectFile()
  }

  const handleReset = () => {
    setFile(null)
    reset()
  }

  const handleCompress = async () => {
    if (!file) return
    start()
    try {
      const result = await compressImage(file, format, quality, setProgress)
      finish(result.blob, outputFileName(file.name, format))
    } catch (err) {
      console.error('Image compression failed:', err)
      fail(err instanceof Error ? err.message : t('imgFailed'))
    }
  }

  if (status === 'idle') {
    return (
      <FileUploader
        accept={['.jpg', '.jpeg', '.png', '.webp']}
        maxSizeMB={MAX_IMAGE_MB}
        onFileSelect={handleFile}
      />
    )
  }

  if (status === 'processing') {
    return (
      <div className="rounded-xl border bg-card p-6 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <ImageDown className="h-5 w-5 text-primary" aria-hidden />
          <p className="font-medium">{t('imgProcessing')}</p>
        </div>
        <ProgressBar value={progress} label={t('imgProgress')} />
      </div>
    )
  }

  if (status === 'done' && resultBlob && file) {
    return (
      <ImageResultCard
        originalName={outputFileName(file.name, format)}
        originalSize={file.size}
        result={{ blob: resultBlob }}
        onReset={handleReset}
      />
    )
  }

  if (status === 'error') {
    return (
      <div className="space-y-4">
        <ErrorMessage message={error ?? t('commonErrorDefault')} onRetry={file ? handleCompress : undefined} />
        {file && <FileInfo file={file} onRemove={handleReset} />}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {file && <FileInfo file={file} onRemove={handleReset} />}

      <div className="rounded-xl border bg-card p-6 shadow-sm">
        <div className="grid gap-6 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="output-format">{t('imgOutputFormat')}</Label>
            <Select
              value={format}
              onValueChange={(value: ImageOutputFormat) => setFormat(value)}
            >
              <SelectTrigger id="output-format" className="w-full">
                <SelectValue placeholder={t('imgSelectFormat')} />
              </SelectTrigger>
              <SelectContent>
                {FORMAT_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {formatLabel(option.value)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="quality">{t('imgQuality')}</Label>
              <span className="font-mono text-sm text-muted-foreground">{quality}%</span>
            </div>
            <Slider
              id="quality"
              value={[quality]}
              min={10}
              max={100}
              step={5}
              onValueChange={(values) => setQuality(values[0] ?? 75)}
              className="mt-4"
            />
          </div>
        </div>

        <Button onClick={handleCompress} className="mt-6 w-full" size="lg">
          <ImageDown className="mr-1" aria-hidden />
          {t('imgAction')}
        </Button>
      </div>
    </div>
  )
}
